/**
 * Terminal login card: optional referral-code capture plus the wallet connect button.
 *
 * The referral form is owned HERE, not by `SuiQueryContext`. Hosted in the
 * provider, every keystroke (validated in `mode: "all"`) re-rendered the provider
 * and so every context consumer on the page, which is what made typing lag.
 */
import { zodResolver } from "@hookform/resolvers/zod";
import { Alert } from "@nextui-org/alert";
import clsx from "clsx";
import { useForm } from "react-hook-form";
import { z } from "zod";

import { ConnectWalletButton } from "@/components/wallet/ConnectWalletButton";
import { useSuiQueryContext } from "@/context/SUIQuery/useSuiQueryContext";
import { storeReferralCode } from "@/lib/referral";
import { Button } from "@/ui/nextui/Button/Button";
import { Card } from "@/ui/nextui/Card/Card";
import { Input } from "@/ui/nextui/Input/Input";
import { Form, FormControl, FormField, FormItem, FormLabel, FormMessage } from "@/ui/shadcn/form";

import { ReferralCodeFormSchema } from "../Dashboard/Dashboard.types";
import { TerminalLoginProps } from "./TerminalLogin.types";

/** Landing / gate screen shown until a referral code is stored (dashboard) or always (home). */
export const TerminalLogin: React.FC<TerminalLoginProps> = ({ className, isReferralCodeSet }) => {
  const { setIsReferralCodeSet } = useSuiQueryContext();

  // Validate on submit, then re-validate on change (RHF default) so an error
  // clears as the user fixes it — no resolver run on every first-pass keystroke.
  // `""` default keeps the input controlled from the first render.
  const referralCodeForm = useForm<z.infer<typeof ReferralCodeFormSchema>>({
    resolver: zodResolver(ReferralCodeFormSchema),
    defaultValues: { referralCode: "" },
    mode: "onSubmit",
    reValidateMode: "onChange",
  });

  function onSubmit(values: z.infer<typeof ReferralCodeFormSchema>) {
    // Schema mirrors `isValidReferralCode`, so this only fails when storage is
    // blocked/full; surface it rather than leaving the button looking dead.
    if (!storeReferralCode(values.referralCode)) {
      referralCodeForm.setError("referralCode", {
        message: "Could not save the referral code in this browser.",
      });

      return;
    }

    setIsReferralCodeSet(true);
  }

  return (
    <section className={clsx(className)} id="terminal-login">
      <div className="container">
        <div className="flex">
          <div className="hidden flex-col items-center justify-center sm:flex sm:w-6/12">
            <img alt="agent-license" loading="lazy" src="/agent-license-face.gif" />
            <img alt="agent-license" loading="lazy" src="/agent-license-flicker.gif" />
          </div>
          <div className="flex min-h-screen w-full flex-col items-center justify-center sm:w-6/12">
            <Card className="w-full sm:w-[410px]">
              <Card.Header className="block">
                <h1 className="mb-0 text-3xl">Terminal Login</h1>
                <p>Connect Your Wallet Below To Enter</p>
              </Card.Header>
              <Card.Body className="py-4">
                {!isReferralCodeSet && (
                  <>
                    <Alert
                      classNames={{
                        base: "mb-4",
                        title: "mb-2",
                      }}
                      color="warning"
                      title="You need to get a valid Referral Code to purchase an Agent License"
                    />

                    <Form {...referralCodeForm}>
                      <form
                        className={clsx("mb-8", className)}
                        id="terminal-login-referral-form"
                        onSubmit={referralCodeForm.handleSubmit(onSubmit)}
                      >
                        <FormField
                          control={referralCodeForm.control}
                          name="referralCode"
                          render={({ field, fieldState }) => (
                            <FormItem>
                              <FormLabel>Referral Code</FormLabel>
                              <FormControl>
                                <Input
                                  autoCapitalize="off"
                                  autoComplete="off"
                                  autoCorrect="off"
                                  className="bg-green-700 [&_input]:text-center"
                                  endContent={
                                    <Button
                                      id="terminal-login-referral-submit"
                                      size="sm"
                                      type="submit"
                                    >
                                      Submit
                                    </Button>
                                  }
                                  isInvalid={!!fieldState.error}
                                  id="terminal-login-referral-input"
                                  placeholder="Type in your referral code..."
                                  spellCheck="false"
                                  size="lg"
                                  variant="bordered"
                                  {...field}
                                />
                              </FormControl>

                              <FormMessage />
                            </FormItem>
                          )}
                        />
                      </form>
                    </Form>
                  </>
                )}

                <ConnectWalletButton className="w-full" />
              </Card.Body>
              <Card.Footer>
                <p className="mb-0 text-center text-sm text-gray-500">
                  By clicking connect wallet, you agree to our Terms of Service and Privacy Policy.
                </p>
              </Card.Footer>
            </Card>
          </div>
        </div>
      </div>
    </section>
  );
};
